import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Edge function calls use fetchWithAuth, which falls back to the anon key when
 * getSession() has no access_token — that yields "Invalid JWT" for function JWT checks.
 * Refresh + explicit Bearer avoids that.
 */
export async function getUserAccessTokenForFunctions(): Promise<string> {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error('You must be logged in');
  }

  const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
  let token = refreshData.session?.access_token;
  if (!token) {
    const { data: sessionData } = await supabase.auth.getSession();
    token = sessionData.session?.access_token;
  }

  const trimmed = token?.trim();
  if (!trimmed) {
    throw new Error(
      refreshError?.message ??
        'No access token available. Try signing out and signing in again.',
    );
  }

  return trimmed;
}

/** `instanceof FunctionsHttpError` breaks across bundles; use `response` from `invoke` or duck-type `context`. */
function responseFromInvokeFailure(
  invokeError: unknown,
  fnResponse?: Response | null,
): Response | null {
  if (fnResponse instanceof Response) return fnResponse;
  if (
    typeof invokeError === 'object' &&
    invokeError !== null &&
    'context' in invokeError &&
    (invokeError as { context: unknown }).context instanceof Response
  ) {
    return (invokeError as { context: Response }).context;
  }
  return null;
}

export async function formatEdgeFunctionInvokeError(
  invokeError: unknown,
  fnResponse?: Response | null,
): Promise<string> {
  const res = responseFromInvokeFailure(invokeError, fnResponse);
  if (res) {
    try {
      const text = (await res.clone().text()).trim();
      if (text) {
        try {
          const j = JSON.parse(text) as {
            error?: string;
            message?: string;
            details?: unknown;
          };
          const parts: string[] = [];
          if (j.error) parts.push(j.error);
          if (j.message && j.message !== j.error) parts.push(j.message);
          if (j.details !== undefined) {
            parts.push(
              typeof j.details === 'string' ? j.details : JSON.stringify(j.details),
            );
          }
          if (parts.length > 0) return parts.join(' — ');
        } catch {
          return text;
        }
      }
    } catch {
      /* ignore */
    }
  }
  if (invokeError instanceof Error && invokeError.message) {
    return invokeError.message;
  }
  if (typeof invokeError === 'object' && invokeError !== null && 'message' in invokeError) {
    return String((invokeError as { message: unknown }).message);
  }
  return String(invokeError ?? 'Request failed');
}

export function formatUnknownError(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === 'object' && err !== null && 'message' in err) {
    return String((err as { message: unknown }).message);
  }
  return String(err);
}

/** `upsert` (default): push row to Algolia and set `markdown_files.algolia`. `delete`: remove Algolia object (row must still exist for RLS). */
export type MarkdownAlgoliaEdgeAction = 'upsert' | 'delete';

export type MarkdownAlgoliaEdgeResult =
  | { ok: true; message?: string; document?: MarkdownFile }
  | { ok: false; message: string };

export async function invokeMarkdownAlgoliaEdge(
  documentId: string,
  options?: { action?: MarkdownAlgoliaEdgeAction },
): Promise<MarkdownAlgoliaEdgeResult> {
  const action = options?.action ?? 'upsert';
  try {
    const accessToken = await getUserAccessTokenForFunctions();
    const { data: result, error: invokeError, response: fnResponse } = await supabase.functions.invoke<{
      success?: boolean;
      buildId?: string;
      message?: string;
      documentId?: string;
      error?: string;
      document?: MarkdownFile;
    }>('index-to-algolia', {
      body: { documentId, action },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (invokeError) {
      const detail = await formatEdgeFunctionInvokeError(invokeError, fnResponse);
      return { ok: false, message: detail || 'Edge function request failed' };
    }

    if (result && typeof result === 'object' && 'error' in result && result.error) {
      return { ok: false, message: String(result.error) };
    }

    if (!result || typeof result !== 'object' || result.success !== true) {
      return { ok: false, message: 'Unexpected response from index-to-algolia' };
    }

    return {
      ok: true,
      message: typeof result.message === 'string' ? result.message : undefined,
      document: result.document,
    };
  } catch (e) {
    return { ok: false, message: formatUnknownError(e) };
  }
}

/** Vector index state on `markdown_files.indexed` (set by index-document). */
export type MarkdownIndexedStatus = 'yes' | 'no' | 'failed' | 'pending';

export const MARKDOWN_INDEXED_STATUSES: MarkdownIndexedStatus[] = [
  'yes',
  'no',
  'failed',
  'pending',
];

export function parseMarkdownIndexedStatus(value: unknown): MarkdownIndexedStatus {
  if (value === 'yes' || value === 'no' || value === 'failed' || value === 'pending') {
    return value;
  }
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return 'no';
}

/** Same four-state lifecycle as `indexed` (column `algolia`, set by index-to-algolia). */
export const parseMarkdownAlgoliaStatus = parseMarkdownIndexedStatus;

export interface MarkdownFile {
  id: string;
  title: string;
  content: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  /** Vector embeddings lifecycle: `yes` | `no` | `failed` | `pending` (column `indexed`, index-document). */
  indexed?: MarkdownIndexedStatus;
  /** Algolia record lifecycle: same values (column `algolia`, index-to-algolia). */
  algolia?: MarkdownIndexedStatus;
  tags?: string[];
  summary?: string | null;
  state?: string;
  jurisdiction?: string;
}
